import { useEffect, useState } from 'react';
import { Loader2, Save, Trash2 } from 'lucide-react';
import {
    useUpsertCromosAlbumSpecies,
    useDeleteCromosAlbumSpecies,
    type CromosAlbumRarity,
} from '../hooks/useSpeciesAdmin';

const CROMOS_RARITIES: CromosAlbumRarity[] = ['legendario', 'épico', 'raro', 'común', 'morralla'];

interface SpeciesCromosAlbumPanelProps {
    scientificName: string;
    inCromosAlbum: boolean;
    cromosDisplayName?: string | null;
    cromosRarity?: string | null;
    defaultWaterType: 'salada' | 'dulce' | 'both';
}

export function SpeciesCromosAlbumPanel({
    scientificName,
    inCromosAlbum,
    cromosDisplayName,
    cromosRarity,
    defaultWaterType,
}: SpeciesCromosAlbumPanelProps) {
    const upsertMutation = useUpsertCromosAlbumSpecies();
    const deleteMutation = useDeleteCromosAlbumSpecies();
    const [displayName, setDisplayName] = useState(cromosDisplayName ?? '');
    const [waterType, setWaterType] = useState<'salada' | 'dulce'>(
        defaultWaterType === 'dulce' ? 'dulce' : 'salada'
    );
    const [rarity, setRarity] = useState<CromosAlbumRarity>(
        (cromosRarity as CromosAlbumRarity) || 'común'
    );
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setDisplayName(cromosDisplayName ?? '');
        setRarity((cromosRarity as CromosAlbumRarity) || 'común');
        setWaterType(defaultWaterType === 'dulce' ? 'dulce' : 'salada');
    }, [cromosDisplayName, cromosRarity, defaultWaterType]);

    const handleSave = async () => {
        setError(null);
        setMessage(null);
        try {
            await upsertMutation.mutateAsync({
                p_scientific_name: scientificName,
                p_display_name: displayName.trim(),
                p_water_type: waterType,
                p_rarity: rarity,
            });
            setMessage('Entrada del álbum Cromos guardada');
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Error al guardar');
        }
    };

    const handleRemove = async () => {
        setError(null);
        setMessage(null);
        try {
            await deleteMutation.mutateAsync({ p_scientific_name: scientificName });
            setDisplayName('');
            setMessage('Eliminada del álbum Cromos');
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Error al eliminar');
        }
    };

    const isBusy = upsertMutation.isPending || deleteMutation.isPending;

    return (
        <div className="space-y-4">
            <div>
                <h4 className="text-sm font-semibold text-foreground">Álbum Cromos (lista canónica)</h4>
                <p className="text-xs text-muted-foreground mt-1">
                    Configuración en <code className="text-xs">species_catalog</code> (flag En álbum Cromos).
                    Rankings y pantalla Cromos leen el pool desde el catálogo.
                </p>
            </div>

            {inCromosAlbum && (
                <span className="inline-flex text-xs px-2 py-1 rounded bg-primary/10 text-primary font-medium">
                    Incluida en álbum Cromos
                </span>
            )}

            {message && <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-sm">{message}</div>}
            {error && <div className="p-2 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-medium mb-1">Nombre visible</label>
                    <input
                        type="text"
                        value={displayName}
                        onChange={e => setDisplayName(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Agua (Cromos)</label>
                    <select
                        value={waterType}
                        onChange={e => setWaterType(e.target.value as 'salada' | 'dulce')}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    >
                        <option value="salada">Salada</option>
                        <option value="dulce">Dulce</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Rareza Cromos</label>
                    <select
                        value={rarity}
                        onChange={e => setRarity(e.target.value as CromosAlbumRarity)}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    >
                        {CROMOS_RARITIES.map(r => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isBusy || !displayName.trim()}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                    {upsertMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {inCromosAlbum ? 'Actualizar en Cromos' : 'Añadir a Cromos'}
                </button>
                {inCromosAlbum && (
                    <button
                        type="button"
                        onClick={handleRemove}
                        disabled={isBusy}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-destructive bg-destructive/10 hover:bg-destructive/20 disabled:opacity-50"
                    >
                        {deleteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        Quitar de Cromos
                    </button>
                )}
            </div>
        </div>
    );
}
